import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Button, Link, Typography17, Typography34, Typography60, useModal, useToast } from "@slid/slid-ips";
import { trackEvent } from "utils/eventTracking";
import { eventTypes } from "types/eventTracking";

type DiscountCode = {
  code: string;
  type: "annual" | "monthly";
};

const ANNUAL_DISCOUNT_CODE = "TUTORIAL_12MO_COUPON";
const MONTHLY_DISCOUNT_CODE = "TUTORIAL_1MO_COUPON";

export const OnboardingPromotionModal = () => {
  const { t } = useTranslation("Modal");
  const { closeModal } = useModal();
  const { showToast } = useToast();

  useEffect(() => {
    trackEvent({
      eventType: eventTypes.show.TUTORIAL_COMPLETED_DISCOUNT_MODAL,
    });
  }, []);

  const discountCodeList: DiscountCode[] = [
    {
      code: ANNUAL_DISCOUNT_CODE,
      type: "annual",
    },
    {
      code: MONTHLY_DISCOUNT_CODE,
      type: "monthly",
    },
  ];

  const clickDiscountCode = async (code: string, type: "annual" | "monthly") => {
    await navigator.clipboard.writeText(code);
    showToast(t("OnboardingPromotionModal.copiedText", { keySeparator: ".", length: type === "annual" ? "12" : "1" }));
    trackEvent({
      eventType: eventTypes.click.GET_DISCOUNT_WITH_THIS_LINK_IN_TUTORIAL_COMPLETED_DISCOUNT_MODAL,
      eventProperties: {
        type: type,
      },
    });
  };

  return (
    <ModalContainer>
      <TextContainer>
        <Typography60 weight={700} color="--gray17" text={"🥳"} textAlign="center" />
        <Typography34 weight={700} color="--gray17" text={t("OnboardingPromotionModal.title", { keySeparator: "." })} textAlign="center" />
        <Typography17 weight={400} color="--gray15" text={t("OnboardingPromotionModal.description", { keySeparator: "." })} />
      </TextContainer>
      <DiscountCodeContainer>
        {discountCodeList.map((discountCode) => (
          <SingleCodeContainer key={discountCode.code} onClick={async () => await clickDiscountCode(discountCode.code, discountCode.type)}>
            <Typography17 weight={700} color="--blue8" text={t("OnboardingPromotionModal.discountCodeText", { keySeparator: ".", length: discountCode.type === "annual" ? "12" : "1" })} />
            <Link linkText={discountCode.code} newTab={false} href={`#`} style={{ cursor: "pointer" }} />
          </SingleCodeContainer>
        ))}
      </DiscountCodeContainer>

      <Button
        appearance="primary"
        size="large"
        text={t("OnboardingPromotionModal.primaryButton", { keySeparator: "." })}
        marginTop="48px"
        callback={() => {
          closeModal();
        }}
      />
    </ModalContainer>
  );
};

const ModalContainer = styled.div`
  width: 464px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 36px;

  background-color: var(--gray1);
  box-shadow: var(--boxShadow3);
  border-radius: 8px;
`;

const DiscountCodeContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;

  gap: 4px;
  margin-top: 24px;
`;

const SingleCodeContainer = styled.div`
  display: flex;
  gap: 4px;
  cursor: pointer;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;
